import React, { useRef, useState } from "react";

import { format } from "date-fns";
import { AnimatePresence, m } from "framer-motion";
import { useHover } from "usehooks-ts";

import { useWorkspaceUsers } from "src/api/taktile/queries";
import { Dataset } from "src/api/types";
import { Pill } from "src/base-components/Pill";
import { IncompatibleLabel } from "src/datasets/DatasetList/IncompatibleLabel";
import { ItemDropdown } from "src/datasets/DatasetList/ItemDropdown";
import { SourceIcon } from "src/datasets/DatasetList/SourceIcon";
import { separateFilenameExtension } from "src/datasets/utils";
import { Avatar } from "src/design-system/Avatar";
import { Tooltip } from "src/design-system/Tooltip";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";
import { dateFromNow } from "src/utils/datetime";
import { formatNumber } from "src/utils/numbers";

type Props = {
  dataset: Dataset;
  incompatible: boolean;
  onDelete: () => void;
  onClick: () => void;
  disableDownloads: boolean;
};

const TypePill: React.FC<{ purpose: Dataset["purpose"] }> = ({ purpose }) => {
  return (
    <m.div
      key="pill"
      animate={{
        opacity: 1,
        transition: {
          type: "tween",
          ease: "easeOut",
          delay: 0.15,
        },
      }}
      className="flex items-center justify-center"
      initial={{ opacity: 0 }}
      transition={{ type: "tween", ease: "easeOut" }}
      layout
    >
      <Pill size="sm" variant="gray">
        <Pill.Text>
          {purpose === "simulation" ? "Simulation" : "Test"}
        </Pill.Text>
      </Pill>
    </m.div>
  );
};

export const Item: React.FC<Props> = ({
  dataset,
  incompatible,
  onClick,
  onDelete,
  disableDownloads,
}) => {
  const { name: fileName } = separateFilenameExtension(dataset.name);
  const hoverRef = useRef<HTMLLIElement | null>(null);
  const isHover = useHover<HTMLLIElement>(hoverRef);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);

  const wasCreatedThisYear =
    new Date(dataset.created_at).getFullYear() === new Date().getFullYear();

  const { orgId, workspace } = useAuthoringContext();
  const users = useWorkspaceUsers(orgId, workspace.id, {
    include_deactivated: true,
  });
  const lastEditedByUser =
    users.data && users.data.find((u) => u.id === dataset.updated_by_id);

  const simulationAccess = useSimulationAccess();
  return (
    <m.li
      ref={hoverRef}
      className="group cursor-pointer rounded-lg border border-gray-100 bg-white px-4 pt-2 pb-3 shadow-sm"
      data-loc={`dataset-entry-${dataset.name}`}
      layout
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-x-2">
        <div className="flex h-7.5 flex-1 items-center gap-x-1 border border-transparent">
          <SourceIcon source={dataset.source} />
          <p className="line-clamp-1 font-inter-semibold-13px text-gray-800">
            {fileName}
          </p>
        </div>
        <div className="flex items-center gap-x-2">
          <AnimatePresence initial={false}>
            {incompatible && (
              <IncompatibleLabel
                datasetId={dataset.id}
                open={tooltipIsOpen}
                onOpenTooltip={setTooltipIsOpen}
              />
            )}
            {simulationAccess.isConfigured && (
              <TypePill purpose={dataset.purpose} />
            )}
            <ItemDropdown
              key="actions"
              dataset={dataset}
              disableDownloads={disableDownloads}
              shouldShowEllipsis={isHover || tooltipIsOpen}
              onDelete={onDelete}
            />
          </AnimatePresence>
        </div>
      </div>
      <p className="ml-6 flex items-center gap-x-2 truncate font-inter-normal-12px text-gray-500">
        {typeof dataset.row_count === "number" ? (
          <span>
            {formatNumber(dataset.row_count)} test{" "}
            {dataset.row_count === 1 ? "case" : "cases"}
          </span>
        ) : null}
        <span className="text-2xl">·</span>{" "}
        {dataset.purpose !== "simulation" && (
          <>
            <Tooltip
              body={
                <div>
                  <span className="text-gray-400">
                    Last edited {dateFromNow(dataset.updated_at)} by{" "}
                  </span>
                  {lastEditedByUser && (
                    <div className="mt-1 flex items-center gap-x-2">
                      <Avatar size="lg" user={lastEditedByUser} />
                      <p className="font-inter-semibold-13px text-white">
                        {lastEditedByUser.full_name}
                      </p>
                    </div>
                  )}
                </div>
              }
              disabled={!lastEditedByUser}
              placement="bottom"
            >
              <span>Last edited {dateFromNow(dataset.updated_at)} </span>
            </Tooltip>
            <span className="text-2xl">·</span>{" "}
          </>
        )}
        {dataset.source === "file" ? "Uploaded" : "Created"} on{" "}
        {format(
          new Date(dataset.created_at),
          wasCreatedThisYear ? "d MMM" : "d MMM yy",
        )}
      </p>
    </m.li>
  );
};
